import { useCallback, useEffect, useRef, useState } from "react";
import logoImage from "../../assets/img/kono-logo.svg";
import { SearchBar } from "./SearchBar";
import { sendAuthenticatedRequest } from "../../common/functions";
import SearchResultsView from "./SearchResultsView";
import { useLocation, useNavigate } from "react-router-dom";
import { TopBar } from "./TopBar";
import { AppliedFilter } from "./Filters";
import { isEqual } from "lodash";
import profilePicPlaceholder from "../../assets/img/avatar-placeholder.svg";
import useFirebaseAuthentication from "../../common/hooks/useFirebaseAuthentication";
import {
  getMismatchType,
  MISMATCH_GRADE,
  setActiveMatchRankingRules,
} from "./helpers";
import { collection, onSnapshot, query, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import {
  SearchHistoryItem,
  useExperimentFlags,
  useRfqQueries,
  useSearchHistory,
  useSearchResultsCache,
  useShapes,
} from "../../buyerStateDefs";
import { Product } from "../../common/models/product";
import { useHelpAndRfq } from "../../buyerStateDefs";
import { getTrackingIds } from "../../visitorTracking";
import { ContinueWhereLeftOff } from "./ContinueWhereLeftOff";
import { DealsForYou } from "./DealsForYou";
import {
  cachedBuyerPreferences,
  loadBuyerPreferences,
  specsProfileFilters,
  useBuyerExperience,
} from "../../buyerCare";

type SearchResponse = {
  results: { _source: Product; _id?: string }[];
  structure: any;
  matchRankingRules?: any;
  totalResults?: number;
  addedSearchHistoryDocId?: string;
};

/**
 * A search hit's `_source` is the product, but only the envelope's `_id`
 * (`supplierId__productId`) says which vendor owns it — the indexed source
 * doesn't carry supplierId. It is recovered here so the result dedupe can
 * fold one vendor's spec-package variants of the same item into one row.
 */
const unwrapSearchHit = (hit: { _source: Product; _id?: string }): Product => {
  const id = String(hit._id || "");
  const split = id.indexOf("__");
  if (split <= 0) return hit._source;
  return { ...hit._source, supplierId: id.slice(0, split) };
};

/**
 * The filters that survive a round trip through the URL. Ticking a filter
 * navigates, and this list is read back on the way in — anything missing from it
 * is silently dropped, so the box stays ticked while the results ignore it.
 */
const FILTERS_IN_URL = [
  "shape",
  "material",
  "requireStandards",
  "dfarsCompliant",
  "domesticMelt",
  "singleHeat",
  "mtrAvailable",
];

const getSearchSessionKey = (
  query: string,
  appliedFilters: AppliedFilter[],
) => {
  return JSON.stringify({ query, appliedFilters });
};

const isAbortError = (error: unknown) => {
  return (
    (error instanceof DOMException && error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError")
  );
};

/**
 * The server's friendly message when a browse limit was reached (HTTP 429
 * from the anti-scraping guard), or null for any other failure. sendRequest
 * throws `Error("<status> <body>")`, so the JSON body rides in the message.
 */
const rateLimitMessage = (error: unknown): string | null => {
  const msg = error instanceof Error ? error.message : String(error);
  if (!msg.startsWith("429")) return null;
  // Only OUR guard's refusal is a browsing limit; it always says so in the
  // body. Any other 429 (an upstream service throttling us, passed through)
  // is a search hiccup and must not be presented as the buyer's fault.
  try {
    const body = JSON.parse(msg.slice(3).trim());
    if (body?.error !== "rate_limited") return null;
    return (
      body.message ||
      "You have reached the browsing limit. Please try again later."
    );
  } catch {
    return null;
  }
};

const SEARCH_HICCUP_MESSAGE =
  "Search is briefly unavailable. Please try again in a moment.";

/** A buyer-readable line for a failed search that was not a browsing limit. */
const searchFailureMessage = (error: unknown): string => {
  const msg = error instanceof Error ? error.message : String(error);
  const body = msg.replace(/^\d{3}\s*/, "").trim();
  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.message === "string" && parsed.message.trim()) {
      return parsed.message.trim();
    }
  } catch {
    // Not JSON (network failure, HTML error page): fall through.
  }
  return SEARCH_HICCUP_MESSAGE;
};

function InventorySearch() {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [structuredQuery, setStructuredQuery] = useState<any>();
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  // The server's message when a browse limit was hit (guest budgets etc.).
  const [limitNotice, setLimitNotice] = useState("");
  // A failed first page (not a browsing limit): shown with a retry button.
  const [searchNotice, setSearchNotice] = useState("");
  // A failed "load more" page: infinite scroll stays off until the buyer
  // retries explicitly, so one bad page cannot turn into a request loop.
  const [loadMoreError, setLoadMoreError] = useState("");
  const loadingMoreRef = useRef(false);
  const activeSearchSessionKeyRef = useRef("");
  const searchAbortControllerRef = useRef<AbortController | null>(null);
  const loadMoreAbortControllerRef = useRef<AbortController | null>(null);
  const addSearchQueryHistory = useSearchHistory(
    (state) => state.addSessionSearchQuery,
  );
  const searchResultsCache = useSearchResultsCache(
    (state) => state.searchResultsCache,
  );
  const setSearchResultsCache = useSearchResultsCache(
    (state) => state.setSearchResultsCache,
  );
  const shapes = useShapes((state) => state.shapes);
  const setShapes = useShapes((state) => state.setShapes);
  const [appliedFilters, setAppliedFilters] = useState<AppliedFilter[]>([]);
  const [selectedFilters, setSelectedFilters] = useState<AppliedFilter[]>([]);
  const setRfqQueries = useRfqQueries((state) => state.setRfqQueries);
  const setTotalUserSearchHistory = useSearchHistory(
    (state) => state.setTotalUserSearchHistory,
  );
  const { authUser } = useFirebaseAuthentication();
  const experience = useBuyerExperience();
  // Saved specs profile: queries this visit already started pre-filtered
  // (or the buyer cleared the filters), so we do not re-apply them.
  const specsAppliedForRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (authUser && experience.savedSpecsProfile) loadBuyerPreferences(authUser.uid);
  }, [authUser, experience.savedSpecsProfile]);
  const dummySearchExperimentFlag = useExperimentFlags(
    (state) => state.dummySearchExperimentVariant,
  );
  const openRfq = useHelpAndRfq((s) => s.openRfq);
  const openHelp = useHelpAndRfq((s) => s.openHelp);

  const locaton = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authUser) return;

    const collectionRef = collection(
      db,
      `users/${authUser.uid}/user-search-history`,
    );
    const q = query(collectionRef);
    const unsubscribe = onSnapshot(q, (snap) => {
      const remoteSearchHistory = snap.docs.map(
        (d) => ({ id: d.id, ...(d.data() as object) }) as SearchHistoryItem,
      );
      setTotalUserSearchHistory(remoteSearchHistory);
    });

    return () => unsubscribe();
  }, [authUser]);

  useEffect(() => {
    return () => {
      searchAbortControllerRef.current?.abort();
      loadMoreAbortControllerRef.current?.abort();
    };
  }, []);

  const handleRfqUpload = (localRfqQueries: string[]) => {
    setRfqQueries(localRfqQueries);
    if (localRfqQueries.length > 0) {
      navigate("/?q=" + localRfqQueries[0]);
    }
  };

  useEffect(() => {
    // We preload all shapes before the user starts searching (or together with the first search)
    // to avoid a delay when the user selects a shape filter
    if (shapes.length > 0) {
      return;
    }
    sendAuthenticatedRequest(
      `${process.env.REACT_APP_SEARCH_JS_API_BASE_URL}/shapes`,
      {},
    )
      .then((response) => response.json())
      .then((data) => {
        setShapes(data.shapes);
      });
  }, [authUser]);

  const fetchSearchPage = useCallback(
    async (options: {
      query: string;
      appliedFilters: AppliedFilter[];
      page: number;
      structuredQuery?: any;
      signal?: AbortSignal;
    }): Promise<SearchResponse> => {
      const { query, appliedFilters, page, structuredQuery, signal } = options;
      return sendAuthenticatedRequest(
        process.env.REACT_APP_SEARCH_JS_API_BASE_URL || "",
        {
          method: "POST",
          signal,
          jsonBody: {
            query,
            appliedFilters,
            page,
            structuredQuery,
            // Lets the backend tie this search to the visitor's later product
            // views, cart adds and orders (vendor score cards).
            tracking: getTrackingIds(),
          },
        },
      ).then(async (resp) => {
        console.log("Structure response retrieved");
        const body: SearchResponse = await resp.json();
        // Adopt the match/ranking rules the server ranked with, so badges and
        // client-side ordering agree exactly with the backend.
        if (body?.matchRankingRules) {
          setActiveMatchRankingRules(body.matchRankingRules);
        }
        return body;
      });
    },
    [],
  );

  const search = useCallback(
    async (query: string, appliedFilters: AppliedFilter[]) => {
      console.log(`Searching for ${query}`);
      // The experiment flag is logged here but not actually used to modify behavior.
      // This is just to track the experiment in PostHog.
      console.log("Search Experiment Flag:", dummySearchExperimentFlag);
      if (!query) {
        return;
      }
      const searchSessionKey = getSearchSessionKey(query, appliedFilters);
      activeSearchSessionKeyRef.current = searchSessionKey;
      searchAbortControllerRef.current?.abort();
      loadMoreAbortControllerRef.current?.abort();
      const abortController = new AbortController();
      searchAbortControllerRef.current = abortController;
      addSearchQueryHistory(query);
      setLoading(true);
      setLimitNotice("");
      setSearchNotice("");
      setLoadMoreError("");
      setLoadingMore(false);
      loadingMoreRef.current = false;
      setSearchQuery(query);
      setStructuredQuery(undefined);
      setSearchResults([]);
      setTotalResults(0);
      setCurrentPage(0);

      try {
        const searchResponse = await fetchSearchPage({
          query,
          appliedFilters,
          page: 1,
          signal: abortController.signal,
        });

        if (
          abortController.signal.aborted ||
          activeSearchSessionKeyRef.current !== searchSessionKey
        ) {
          return;
        }

        const rankedSearchResults = searchResponse.results.map(unwrapSearchHit);

        const resolvedTotalResults =
          searchResponse.totalResults || rankedSearchResults.length;

        setSearchResults(rankedSearchResults);
        setSearchResultsCache({
          searchQuery: query,
          results: rankedSearchResults,
          totalResults: resolvedTotalResults,
          currentPage: 1,
          structuredQuery: searchResponse.structure,
          appliedFilters,
        });
        setStructuredQuery(searchResponse.structure);
        setAppliedFilters(appliedFilters);
        setTotalResults(resolvedTotalResults);
        setCurrentPage(1);
        await recordResultInfoInHistory(
          query,
          appliedFilters,
          rankedSearchResults,
          searchResponse.structure,
          searchResponse.addedSearchHistoryDocId,
        );
      } catch (error) {
        if (isAbortError(error)) {
          return;
        }
        const limited = rateLimitMessage(error);
        if (limited) {
          setLimitNotice(limited);
          return;
        }
        console.error("Search failed", error);
        if (activeSearchSessionKeyRef.current === searchSessionKey) {
          setSearchNotice(searchFailureMessage(error));
        }
      } finally {
        // Only the run that still owns the abort controller may end the
        // loading state. A superseded run (aborted by a newer search for the
        // same query — e.g. the re-fire when auth resolves) would otherwise
        // flip loading off while the replacement is mid-flight, and that
        // transient "done, zero results" frame auto-opened the help assistant
        // over thousands of perfectly good matches.
        if (searchAbortControllerRef.current === abortController) {
          searchAbortControllerRef.current = null;
          if (activeSearchSessionKeyRef.current === searchSessionKey) {
            setLoading(false);
          }
        }
      }
    },
    [
      addSearchQueryHistory,
      authUser,
      dummySearchExperimentFlag,
      fetchSearchPage,
      setSearchResultsCache,
    ],
  );

  const loadNextPage = useCallback(async () => {
    const searchSessionKey = getSearchSessionKey(searchQuery, appliedFilters);
    if (
      loading ||
      loadingMore ||
      loadingMoreRef.current ||
      loadMoreError ||
      !searchQuery ||
      !structuredQuery ||
      searchResults.length >= totalResults ||
      activeSearchSessionKeyRef.current !== searchSessionKey
    ) {
      return;
    }

    loadMoreAbortControllerRef.current?.abort();
    const abortController = new AbortController();
    loadMoreAbortControllerRef.current = abortController;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      const searchResponse = await fetchSearchPage({
        query: searchQuery,
        appliedFilters,
        page: nextPage,
        structuredQuery,
        signal: abortController.signal,
      });

      if (
        abortController.signal.aborted ||
        activeSearchSessionKeyRef.current !== searchSessionKey
      ) {
        return;
      }

      const structure = searchResponse.structure || structuredQuery;
      const rankedPageResults = searchResponse.results.map(unwrapSearchHit);
      const combinedResults = [...searchResults, ...rankedPageResults];
      // The backend's totalResults is the raw OpenSearch match count (loose
      // fuzzy/should matching), which can be far larger than what is actually
      // servable after filtering hidden/material-less items and the rerank
      // window cap. If a page returns no new results the servable set is
      // exhausted, so pin the total to what we actually have; otherwise
      // hasMoreResults stays true forever and the "load more" spinner loops.
      const resolvedTotalResults =
        rankedPageResults.length === 0
          ? combinedResults.length
          : searchResponse.totalResults || totalResults;

      setSearchResults(combinedResults);
      setStructuredQuery(structure);
      setTotalResults(resolvedTotalResults);
      setCurrentPage(nextPage);
      setSearchResultsCache({
        searchQuery,
        results: combinedResults,
        totalResults: resolvedTotalResults,
        currentPage: nextPage,
        structuredQuery: structure,
        appliedFilters,
      });
    } catch (error) {
      if (isAbortError(error)) {
        return;
      }
      if (activeSearchSessionKeyRef.current !== searchSessionKey) {
        return;
      }
      const limited = rateLimitMessage(error);
      if (limited) {
        setLimitNotice(limited);
        // Paging is over for this search either way; keep the scroll sentinel
        // from asking again.
        setLoadMoreError(
          "Loading more results is paused for now (browsing limit reached).",
        );
        return;
      }
      console.error("Loading next search page failed", error);
      setLoadMoreError(
        `Couldn't load more results. ${searchFailureMessage(error)}`,
      );
    } finally {
      if (loadMoreAbortControllerRef.current === abortController) {
        loadMoreAbortControllerRef.current = null;
      }
      if (activeSearchSessionKeyRef.current === searchSessionKey) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [
    appliedFilters,
    currentPage,
    fetchSearchPage,
    loading,
    loadingMore,
    loadMoreError,
    searchQuery,
    searchResults,
    setSearchResultsCache,
    structuredQuery,
    totalResults,
  ]);

  // The explicit retry after a failed page: clears the failure memory and
  // asks for the page again once, on the buyer's click.
  const retryLoadMoreRef = useRef(false);
  const retryLoadMore = useCallback(() => {
    retryLoadMoreRef.current = true;
    setLoadMoreError("");
  }, []);
  useEffect(() => {
    if (!retryLoadMoreRef.current || loadMoreError) return;
    retryLoadMoreRef.current = false;
    void loadNextPage();
  }, [loadMoreError, loadNextPage]);

  const retrySearch = useCallback(() => {
    if (!searchQuery) return;
    void search(searchQuery, appliedFilters);
  }, [appliedFilters, search, searchQuery]);

  const recordResultInfoInHistory = async (
    query: string,
    appliedFilters: AppliedFilter[],
    searchResults: Product[],
    structuredQuery: any,
    addedSearchHistoryDocId: string | undefined,
  ) => {
    const timestamp = Timestamp.now();
    const minMismatchGrade =
      searchResults.length > 0
        ? Math.min(
            ...searchResults.map((p) => {
              const mismatchType = getMismatchType(p, structuredQuery);
              return MISMATCH_GRADE[
                mismatchType as keyof typeof MISMATCH_GRADE
              ];
            }),
          )
        : undefined;
    const newHistoryItem: SearchHistoryItem = {
      query,
      urlQueryString: window.location.search,
      timestamp,
      numResults: searchResults.length,
      minMismatchGrade,
      appliedFilters,
    };

    if (authUser) {
      await recordResultInfoSignedIn(newHistoryItem, addedSearchHistoryDocId);
    } else {
      await recordResultInfoInHistoryNonSignedIn(newHistoryItem);
    }
  };

  const recordResultInfoInHistoryNonSignedIn = async (
    item: SearchHistoryItem,
  ) => {
    // save to local storage
    const existingHistoryStr = localStorage.getItem(
      "searchHistoryWithoutSignIn",
    );
    const existingHistory = existingHistoryStr
      ? JSON.parse(existingHistoryStr)
      : [];
    localStorage.setItem(
      "searchHistoryWithoutSignIn",
      JSON.stringify([...existingHistory, item]),
    );
  };

  const recordResultInfoSignedIn = async (
    item: SearchHistoryItem,
    addedSearchHistoryDocId: string | undefined,
  ) => {
    if (!authUser) return;
    if (!addedSearchHistoryDocId) return;

    await sendAuthenticatedRequest(
      `${process.env.REACT_APP_SEARCH_JS_API_BASE_URL}/updateSearchHistoryItem`,
      {
        method: "POST",
        jsonBody: {
          docId: addedSearchHistoryDocId,
          historyItem: item,
        },
      },
    )
      .then((resp) => {
        console.log("Search history item updated with result info");
      })
      .catch((err) => {
        console.error("Failed to update search history item", err);
      });
  };

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get("q");
    if (!q) {
      activeSearchSessionKeyRef.current = "";
      searchAbortControllerRef.current?.abort();
      loadMoreAbortControllerRef.current?.abort();
      searchAbortControllerRef.current = null;
      loadMoreAbortControllerRef.current = null;
      setSelectedFilters([]);
      setAppliedFilters([]);
      setSearchQuery("");
      setSearchResults([]);
      setStructuredQuery(undefined);
      setTotalResults(0);
      setCurrentPage(0);
      setLoading(false);
      setLoadingMore(false);
      setSearchNotice("");
      setLoadMoreError("");
      loadingMoreRef.current = false;
      return;
    }

    let filters = Array.from(
      new URLSearchParams(window.location.search).entries(),
    )
      .filter(([key]) => FILTERS_IN_URL.includes(key))
      .map(([key, value]) => ({ filter: key, value }));
    // First search of a query with no filters: start from the saved specs
    // profile (certs, standards) when the buyer has one and the switch is on.
    if (
      !filters.length &&
      authUser &&
      experience.savedSpecsProfile &&
      !specsAppliedForRef.current.has(q)
    ) {
      specsAppliedForRef.current.add(q);
      const prefs = cachedBuyerPreferences(authUser.uid);
      const fromProfile = prefs?.available.specs
        ? specsProfileFilters(prefs.prefs.specs)
        : [];
      if (fromProfile.length) {
        filters = fromProfile;
        const params = new URLSearchParams(window.location.search);
        fromProfile.forEach((f) => params.set(f.filter, f.value));
        window.history.replaceState(null, "", `${window.location.pathname}?${params.toString()}`);
      }
    }
    const searchSessionKey = getSearchSessionKey(q, filters);
    activeSearchSessionKeyRef.current = searchSessionKey;
    searchAbortControllerRef.current?.abort();
    loadMoreAbortControllerRef.current?.abort();
    searchAbortControllerRef.current = null;
    loadMoreAbortControllerRef.current = null;
    setSelectedFilters(filters);

    if (
      searchResultsCache &&
      searchResultsCache.searchQuery === q &&
      isEqual(searchResultsCache.appliedFilters, filters)
    ) {
      console.log("Using cached results");
      setSearchResults(searchResultsCache.results);
      setStructuredQuery(searchResultsCache.structuredQuery);
      setTotalResults(
        searchResultsCache.totalResults || searchResultsCache.results.length,
      );
      setCurrentPage(searchResultsCache.currentPage || 1);
      setSearchQuery(q);
      setAppliedFilters(filters);
      setLoading(false);
      setLoadingMore(false);
      loadingMoreRef.current = false;
      return;
    }

    search(q, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locaton.search, search, searchResultsCache]);

  const fullScreen =
    !searchQuery && !loading && (!searchResults || searchResults.length === 0);

  if (fullScreen) {
    return (
      <div>
        <TopBar
          fullScreen={fullScreen}
          searchQuery={searchQuery}
          userProfileImg={profilePicPlaceholder}
        />
        <div className="mt-[6em] lg:mt-[12em] mb-5">
          <div className="text-center">
            <img src={logoImage} alt="Gaias" className="w-[14em] mx-auto" />
            <div className="mt-11 px-6">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
                The platform for high-performance alloys
              </h1>
              <p className="mt-2 text-lg text-gray-600">
                Source high-performance alloys from the largest online
                inventory in North America. Instantly compare pricing and
                purchase.
              </p>
            </div>
            <div className="mt-7">
              <SearchBar full={fullScreen} onRfqUpload={handleRfqUpload} />
            </div>
            <div className="mt-6 flex flex-wrap justify-center items-center gap-3">
              <button
                type="button"
                onClick={() => openRfq()}
                className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100"
              >
                Upload RFQ
              </button>
              <button
                type="button"
                onClick={() => openHelp()}
                className="rounded-full border-2 border-[#00062e] px-6 py-2 text-sm font-bold text-[#00062e] hover:bg-slate-100"
              >
                Need Help
              </button>
            </div>
            <ContinueWhereLeftOff />
            <DealsForYou />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl">
      <TopBar
        fullScreen={fullScreen}
        searchQuery={searchQuery}
        logoOnClick={() => {
          setSearchQuery("");
          setSearchResults([]);
          setStructuredQuery(undefined);
          setTotalResults(0);
          setCurrentPage(0);
          setLoadingMore(false);
          loadingMoreRef.current = false;
        }}
        userProfileImg={profilePicPlaceholder}
      />
      {!fullScreen && (
        <div className="md:hidden sticky top-0 bg-white z-10">
          <SearchBar
            full={fullScreen}
            initialQuery={searchQuery}
            onRfqUpload={handleRfqUpload}
          />
        </div>
      )}

      {limitNotice && (
        <div className="mx-auto mt-4 max-w-3xl rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <span className="font-medium">Browsing limit reached.</span>{" "}
          {limitNotice}{" "}
          {!authUser && (
            <a href="/signup" className="font-medium underline">
              Create a free account
            </a>
          )}
        </div>
      )}

      {searchNotice && !limitNotice && (
        <div className="mx-auto mt-4 flex max-w-3xl flex-wrap items-center justify-between gap-3 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-800">
          <span>{searchNotice}</span>
          <button
            type="button"
            onClick={retrySearch}
            disabled={loading}
            className="rounded-full border-2 border-[#00062e] px-5 py-1.5 text-sm font-bold text-[#00062e] hover:bg-slate-100 disabled:opacity-50"
          >
            Try again
          </button>
        </div>
      )}

      <SearchResultsView
        products={searchResults}
        structuredQuery={structuredQuery}
        query={searchQuery}
        loading={loading}
        loadingMore={loadingMore}
        totalResults={totalResults}
        onLoadMore={loadNextPage}
        loadMoreError={loadMoreError}
        onRetryLoadMore={retryLoadMore}
        searchFailed={!!searchNotice || !!limitNotice}
        selectedFilters={selectedFilters}
      />
    </div>
  );
}

export default InventorySearch;
